import { last } from 'es-toolkit';
import { SourceTypes } from '../../../../constants';
import { buildProxyURL, formatText, handleResponseError } from '../../utils';
import type { DataValue, PictureValueType, ProviderType } from '../types';
import { tumblrAccountBlogURLRegExp } from './tumblrURLPattern';
import type { TumblrResponseType, TumblrSourceValue } from './types';
import { getTumblrMedia } from './utils';

const PLATFORM = SourceTypes.TUMBLR;

type TumblrDataValue = DataValue<string | PictureValueType[]>;

const select = (response: TumblrResponseType): TumblrDataValue[] => {
  handleResponseError(response, PLATFORM, 'response.posts');

  return response.response.posts.reduce<TumblrDataValue[]>((acc, post) => {
    const {
      blog,
      type,
      summary,
      timestamp,
      id_string: id,
      post_url: postUrl,
      note_count: noteCount
    } = post;
    const avatars = response.response.blog.avatar;
    const prefixedText = type === 'quote' && summary ? `"${summary}"` : summary;
    const text = formatText(prefixedText, PLATFORM);
    const media = getTumblrMedia(post);

    if (text || media?.src || media?.thumbnail) {
      acc.push({
        id,
        text,
        media,
        timestamp: timestamp * 1000,
        source: {
          type: PLATFORM,
          url: postUrl
        },
        user: {
          name: blog.name,
          image: last(avatars)?.url,
          url: blog.url
        },
        counters: {
          likes: noteCount
        }
      });
    }

    return acc;
  }, []);
};

export function createTumblrDataProvider(
  sourceValue: TumblrSourceValue,
  globalKey: string[],
  filtersKey: string[]
): ProviderType<TumblrResponseType> {
  const { source = '' } = sourceValue;

  const key = [...globalKey, ...filtersKey, source];

  const page = (rawData: TumblrResponseType | undefined) => {
    const querySearchParams = new URLSearchParams({
      offset: '0',
      page_number: '1'
    });

    if (rawData) {
      if (!rawData.response?._links?.next) {
        return;
      }

      const { offset, page_number: pageNumber } =
        rawData.response._links.next.query_params;

      querySearchParams.set('offset', offset);
      querySearchParams.set('page_number', pageNumber);
    }

    const [_all, blog] = source.match(tumblrAccountBlogURLRegExp) ?? [];

    return buildProxyURL({
      apiSource: PLATFORM,
      params: { q: `blog/${blog}/posts?${querySearchParams}` }
    });
  };

  return { key, page, select };
}
